"use client";

import { Fragment, createContext, useContext, type ReactNode } from "react";
import {
  AnimatePresence,
  motion,
  useIsPresent,
  useReducedMotion,
} from "motion/react";
import { legacyHeaderMotion } from "./legacy-header-motion";

const lookHeaderMotion = {
  motion: { div: motion.div },
  AnimatePresence,
  useIsPresent,
  useReducedMotion,
};
export type HeaderMotion = typeof lookHeaderMotion;

/**
 * The library a header and its menu animate with: motion/react on the look system, framer-motion on old websites.
 * A presence and the parts it removes must come from the same library, as each keeps its own presence context.
 */
export const headerMotion = (lookSystem: boolean): HeaderMotion =>
  lookSystem ? lookHeaderMotion : legacyHeaderMotion;

const HeaderMotionContext = createContext<HeaderMotion>(legacyHeaderMotion);

/**
 * Hands the menu's parts their library. They take hooks from it, so the subtree mounts afresh when the library changes
 * (a website the CMS preview converts) instead of calling another library's hooks in place.
 */
export function HeaderMotionProvider({
  lookSystem,
  children,
}: {
  lookSystem: boolean;
  children: ReactNode;
}) {
  return (
    <HeaderMotionContext.Provider value={headerMotion(lookSystem)}>
      <Fragment key={lookSystem ? "look" : "legacy"}>{children}</Fragment>
    </HeaderMotionContext.Provider>
  );
}

export const useHeaderMotion = () => useContext(HeaderMotionContext);

/** Whether a header's own menu opens and closes at once: on the look system, for a visitor who prefers less motion. */
export function useStillHeaderMenu(lookSystem: boolean): boolean {
  return useReducedMotion() === true && lookSystem;
}
